import {Button, Dropdown, DropdownItem, getFilePreviewUrl} from 'platform/components';
import {HStack, Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {CustomerMessageAttachmentResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {openFile, suffixTestId, TestIdProps} from 'shared/web';

import {downloadFile} from '../../../utils/downloadFile';
import {isPreviewableAttachment} from '../utils/isPreviewableAttachment';
import {isSafeAttachmentUrl} from '../utils/isSafeAttachmentUrl';

interface ThreadEmailAttachmentProps extends TestIdProps {
  attachment: CustomerMessageAttachmentResponseBody;
}

export function ThreadEmailAttachment(props: ThreadEmailAttachmentProps) {
  const isSafe = isSafeAttachmentUrl(props.attachment.uri);
  const isPreviewable = isSafe && isPreviewableAttachment(props.attachment);

  const handlePreview = () =>
    openFile(getFilePreviewUrl(props.attachment.uri, props.attachment.contentType));

  const handleDownload = () =>
    downloadFile(props.attachment.uri, {
      fileName: props.attachment.filename,
      fileType: 'attachment',
    });

  return match([isSafe, isPreviewable])
    .with([true, true], () => (
      <Dropdown
        placement="bottom-start"
        dropdownControl={
          <HStack>
            <Button
              variant="link"
              size="small"
              leftIcon="action/description"
              title={props.attachment.filename}
              data-testid={suffixTestId('threadEmailAttachment-options', props)}
            />
          </HStack>
        }
        data-testid={suffixTestId('threadEmailAttachment', props)}
      >
        <DropdownItem
          leftIcon="action/visibility"
          label={i18n.t('general.actions.preview')}
          onClick={handlePreview}
          data-testid={suffixTestId('threadEmailAttachment-preview', props)}
        />
        <DropdownItem
          leftIcon="file/download"
          label={i18n.t('general.actions.download')}
          onClick={handleDownload}
          data-testid={suffixTestId('threadEmailAttachment-download', props)}
        />
      </Dropdown>
    ))
    .with([true, false], () => (
      <Button
        variant="link"
        size="small"
        leftIcon="action/description"
        title={props.attachment.filename}
        onClick={handleDownload}
        data-testid={suffixTestId('threadEmailAttachment-download', props)}
      />
    ))
    .otherwise(() => (
      <Text size="xSmall" color="tertiary">
        {props.attachment.filename}
      </Text>
    ));
}
